import { Badge, Box, Flex, Text, Tooltip } from '@chakra-ui/react';

import { IEmailContentType } from '@publishing-engine/domain/models/email-templates';
import RichTextPreview from '../Submission/GenericFieldComponents/RichText/RichTextPreview';
import { removeHrefKeepText } from 'src/utils/email.utils';

interface Props {
  template: IEmailContentType | null;
}

const UNRESOLVED_REGEX =
  /<unresolved>\s*({{\s*[^}]+\s*}})\s*<\/unresolved>|{{\s*[^}]+\s*}}/g;

const Unresolved = ({ value }: { value: string }) => (
  <Tooltip
    hasArrow
    label="No value available for this variable"
    bg="gray.300"
    color="black"
    placement="top"
  >
    <Box as="span" width={'fit-content'}>
      <Badge size="context" colorScheme="red">
        {value}
      </Badge>
    </Box>
  </Tooltip>
);


function renderTextWithUnresolved(text: string) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  const UNRESOLVED_STRIP_REGEX = /<\/?unresolved>/gi;

  text.replace(UNRESOLVED_REGEX, (match, _, index) => {
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }

    const cleaned = match.replace(UNRESOLVED_STRIP_REGEX, '');

    parts.push(<Unresolved key={index} value={cleaned} />);

    lastIndex = index + match.length;
    return match;
  });

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

const renderRecipient = (recipients?: string[] | string) => {
  if (!recipients || (Array.isArray(recipients) && recipients.length === 0)) {
    return '-';
  }

  const text = Array.isArray(recipients)
    ? recipients.join(', ')
    : recipients;

  return renderTextWithUnresolved(text);
};

const getHost = () => {
  try {
    const currentUrl = window.location.href;
    const match = currentUrl.match(/^(.*?)\/app\//);

    return match?.[1] ?? '';
  } catch (_e) {
    return '';
  }
};

export function preprocessUnresolved(html: string): string {
  return html.replace(
    /<unresolved>\s*({{\s*[^}]+\s*}})\s*<\/unresolved>|{{\s*[^}]+\s*}}/gi,
    (match, value) => `
      <span
        class="unresolved-var"
        data-tooltip="No value available for this variable"
      >
        ${value ?? match}
      </span>
    `
  );
}

const PreviewEmail = ({ template }: Props) => {
  if (!template) return null;

  return (
    <Flex
      borderWidth="1px"
      borderStyle="solid"
      borderColor="neutralSecondaryBorder"
      borderRadius="small"
      padding={4}
      flexDirection="column"
      gap={4}
    >
      <Flex flexDirection="column">
        <Text textStyle="headingItem">To</Text>
        <Text textStyle="textBody">
          {renderRecipient(template.recipients?.to)}
        </Text>
      </Flex>

      <Flex flexDirection="column">
        <Text textStyle="headingItem">Cc</Text>
        <Text textStyle="textBody">
          {renderRecipient(template.recipients?.cc)}
        </Text>
      </Flex>

      <Flex flexDirection="column">
        <Text textStyle="headingItem">Bcc</Text>
        <Text textStyle="textBody">
          {renderRecipient(template.recipients?.bcc)}
        </Text>
      </Flex>

      <Flex flexDirection="column">
        <Text textStyle="headingItem">Subject</Text>
        <Text textStyle="textBody">{renderTextWithUnresolved(template.subject)}</Text>
      </Flex>

      <Flex flexDirection="column">
        <Text textStyle="headingItem">Body Text</Text>
        <RichTextPreview value={preprocessUnresolved(removeHrefKeepText(template.body))} />
      </Flex>

      {template.attachments?.length ? (
        <Flex flexDirection="column">
          <Text textStyle="headingItem">Attachments</Text>
          {template.attachments?.map(att => (
            <>
              <Text
                textStyle="textBody"
                as="a"
                href={`${getHost()}/asset-access/?url=${att.url}`}
              >
                {att.name}
              </Text>
            </>
          ))}
        </Flex>
      ) : null}
    </Flex>
  );
};

export default PreviewEmail;
